'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import {
  TimelineControls,
  TimelineControlsProps,
} from 'components/ui/TimelineControls/TimelineControls';
import { CarouselControls } from 'components/ui/CarouselControls/CarouselControls';
import { SwoopAccent } from './SwoopAccent';

import styles from './Timeline.module.scss';
import { ImageWithFocalPoint } from '../../../../wp-blocks/helpers/ImageUpload/ImageUpload';
import { Spacings } from '../../../../wp-blocks/helpers/SpacingControls/RangeControl';
import { withWPStyles } from 'utils/withWPStyles';
import { getFillImageProps } from 'utils/image/getFillImageProps';
import { useSwipeable } from 'react-swipeable';

export type Slide = {
  image: ImageWithFocalPoint;
  textContent: string;
  imageDescription: string;
} & TimelineControlsProps['items'][0];

export type TimelineProps = {
  items: Slide[];
  onClick?: TimelineControlsProps['onChange'];
  spacing?: Spacings;
};

const ITEM_LENGTH = 105;

export const Timeline = ({ items, onClick, spacing }: TimelineProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [delayedActiveIndex, setDelayedActiveIndex] = useState(0);
  const [slideState, setSlideState] = useState<'idle' | 'transitioning'>(
    'idle'
  );
  const [timelineOffset, setTimelineOffset] = useState(0);

  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const slideTimer = useRef<ReturnType<typeof setTimeout>>();

  const onPrev = () => {
    const prevIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    handleSlideTransition(prevIndex);
  };

  const onNext = () => {
    const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
    handleSlideTransition(nextIndex);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => onNext(),
    onSwipedRight: () => onPrev(),
  });

  const timelineHandlers = useSwipeable({
    onSwiping: e =>
      Number.isFinite(e.deltaX)
        ? transformTimeline(preventOverslide(e.deltaX + timelineOffset))
        : undefined,
    onSwiped: e => {
      setTimelineOffset(prev => preventOverslide(prev + e.deltaX));
    },
  });

  const refPassthrough = el => {
    timelineHandlers.ref(el);
    timelineRef.current = el;
  };

  const onChange = (item: TimelineControlsProps['items'][0], index: number) => {
    handleSlideTransition(index);

    onClick(item, index);
  };

  const handleSlideTransition = index => {
    clearTimeout(slideTimer.current);

    setSlideState('transitioning');
    setActiveIndex(index);

    slideTimer.current = setTimeout(() => {
      setDelayedActiveIndex(index);
      setSlideState('idle');
    }, 150);
  };

  const transformTimeline = (value: number) => {
    if (timelineRef.current) {
      if (value <= 0 && value >= -timelineRef.current.scrollWidth) {
        timelineRef.current.style.transform = `translateX(${value}px)`;
      }
    }
  };

  const preventOverslide = value => {
    if (!timelineRef.current || !containerRef.current || value > 0) {
      return 0;
    }

    if (
      -value >
      timelineRef.current.scrollWidth - containerRef.current.clientWidth
    ) {
      return (
        -timelineRef.current.scrollWidth + containerRef.current.clientWidth
      );
    }

    return value;
  };

  useEffect(() => {
    const containerWidth = containerRef.current.clientWidth;
    const activeItemPosition = activeIndex * ITEM_LENGTH + ITEM_LENGTH;
    const fittingItemsNumber = Math.floor(containerWidth / ITEM_LENGTH);

    if (activeItemPosition > containerWidth) {
      transformTimeline(-ITEM_LENGTH * (activeIndex - fittingItemsNumber + 1));
      setTimelineOffset(-ITEM_LENGTH * (activeIndex - fittingItemsNumber + 1));
    } else {
      transformTimeline(0);
      setTimelineOffset(0);
    }
  }, [activeIndex]);

  useEffect(() => {
    return () => {
      clearTimeout(slideTimer.current);
    };
  }, []);

  return (
    <section
      className={cx(styles.base, withWPStyles(spacing))}
      ref={containerRef}>
      <TimelineControls
        ref={refPassthrough}
        className={styles.timeline}
        items={items.map(item => ({ label: item.label, value: item.value }))}
        onChange={onChange}
        activeIndex={activeIndex}
      />
      <div {...handlers} className={styles.slider}>
        <div className={styles.controls}>
          <CarouselControls.Prev
            onClick={onPrev}
            className={styles.control}
            dark
          />
          <CarouselControls.Next
            onClick={onNext}
            className={styles.control}
            dark
          />
        </div>
        <div className={styles.contentWrapper}>
          <div
            className={styles.content}
            style={{ transform: `translateX(-${delayedActiveIndex}00%)` }}>
            {items.map((item, index) => (
              <div
                key={index}
                className={cx(
                  styles.contentSlide,
                  delayedActiveIndex === index && styles.idle,
                  slideState === 'transitioning' && styles.transitioning
                )}>
                <div
                  className={styles.text}
                  dangerouslySetInnerHTML={{ __html: item.textContent }}
                />
                <div className={styles.imageDescription}>
                  {item.imageDescription}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.rightContent}>
        <div className={styles.yearSlide}>{items[activeIndex].label}</div>
        <div className={styles.imageSlide}>
          <div
            className={styles.imageWrapper}
            style={{ transform: `translateX(-${delayedActiveIndex * 100}%)` }}>
            {items.map((item, index) => (
              <div
                key={index}
                className={cx(
                  styles.image,
                  delayedActiveIndex === index && styles.idle,
                  slideState === 'transitioning' && styles.transitioning
                )}>
                <Image
                  fill
                  sizes="(min-width: 1280px) 376px, (min-width: 970px) 240px, 100vw"
                  {...getFillImageProps(item.image)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
